<template>
  <div class="documents-page">
    <b-overlay
      :show="loading"
      variant="transparent"
      opacity="0.85"
      blur="2px"
      rounded="sm"
      spinner-variant="primary"
    >
      <!-- Back button when viewing a case's documents -->
      <transition
        name="fade"
        mode="out-in"
      >
        <div
          v-if="selectedCase"
          key="detail"
        >
          <button
            class="back-btn"
            @click="selectedCase = null"
          >
            <feather-icon
              icon="ArrowLeftIcon"
              size="18"
            />
            <span>{{ $t('DEBTIA_ALL_CASES') }}</span>
          </button>

          <!-- Selected Case Header -->
          <div class="case-detail-header">
            <div class="detail-header-content">
              <div class="detail-icon">
                <feather-icon
                  icon="FolderIcon"
                  size="22"
                />
              </div>
              <div class="detail-info">
                <h4 class="detail-creditor">
                  {{ selectedCase.creditorName }}
                </h4>
                <div class="detail-meta">
                  <span class="meta-item">{{ $t('DEBTIA_CASE_PREFIX') }} {{ selectedCase.journalNumber }}</span>
                  <span class="meta-sep">&middot;</span>
                  <span class="meta-item">{{ formatCurrency(selectedCase.remainingAmount) }} {{ $t('DEBTIA_REMAINING_BACK') }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Documents List -->
          <div
            v-if="documents.length === 0"
            class="docs-empty"
          >
            <feather-icon
              icon="InboxIcon"
              size="48"
              class="empty-icon"
            />
            <h5>{{ $t('DEBTIA_NO_DOCUMENTS') }}</h5>
            <p>{{ $t('DEBTIA_NO_DOCS_TEXT') }}</p>
          </div>

          <div
            v-else
            class="docs-list"
          >
            <div
              v-for="(doc, index) in documents"
              :key="index"
              class="doc-item"
              @click="handleDocClick(doc)"
            >
              <div class="doc-icon">
                <feather-icon
                  :icon="getDocIcon(doc)"
                  size="20"
                />
              </div>
              <div class="doc-info">
                <span class="doc-name">{{ getDocName(doc) }}</span>
                <span
                  v-if="getDocCategory(doc)"
                  class="doc-category"
                >{{ getDocCategory(doc) }}</span>
              </div>
              <span class="doc-ext">{{ getFileExtension(doc) }}</span>
              <div class="doc-action">
                <feather-icon
                  icon="DownloadIcon"
                  size="16"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Case Cards Grid -->
        <div
          v-else
          key="grid"
        >
          <div class="docs-page-header">
            <h5 class="page-title">
              <feather-icon
                icon="FileTextIcon"
                size="20"
                class="mr-50"
              />
              {{ $t('DEBTIA_DOCUMENTS') }}
              <span class="cases-count">({{ cases.length }} {{ $t('DEBTIA_CASES_LABEL') }})</span>
            </h5>
            <p class="page-subtitle">
              {{ $t('DEBTIA_SELECT_CASE_FOR_DOCS') }}
            </p>
          </div>

          <div
            v-if="cases.length === 0 && !loading"
            class="docs-empty"
          >
            <feather-icon
              icon="InboxIcon"
              size="48"
              class="empty-icon"
            />
            <h5>{{ $t('DEBTIA_NO_RELEVANT_DOCS') }}</h5>
          </div>

          <div
            v-else
            class="cases-grid"
          >
            <div
              v-for="caseItem in cases"
              :key="caseItem.id"
              class="doc-case-card"
              @click="selectCase(caseItem)"
            >
              <div class="card-header-bar">
                <div class="card-creditor">
                  {{ caseItem.creditorName }}
                </div>
                <feather-icon
                  icon="ChevronRightIcon"
                  size="18"
                  class="card-arrow"
                />
              </div>
              <div class="card-details">
                <div class="card-detail-row">
                  <span class="detail-label">{{ $t('DEBTIA_CASE_NUMBER') }}</span>
                  <span class="detail-value">{{ caseItem.journalNumber }}</span>
                </div>
                <div class="card-detail-row">
                  <span class="detail-label">{{ $t('DEBTIA_REMAINING_TO_PAY_DOCS') }}</span>
                  <span class="detail-value amount">{{ formatCurrency(caseItem.remainingAmount) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </transition>
    </b-overlay>
  </div>
</template>

<script>
import {
  BOverlay,
} from 'bootstrap-vue'
import axios from '@axios'
import constants from '@/app-configs/constants'

export default {
  name: 'Documents',
  components: {
    BOverlay,
  },
  data() {
    return {
      loading: true,
      cases: [],
      selectedCase: null,
      documents: [],
      caseDocuments: {},
    }
  },
  created() {
    this.fetchCases()
  },
  methods: {
    async fetchCases() {
      this.loading = true
      this.cases = []
      this.caseDocuments = {}
      let page = 1
      const pageSize = 100
      let allCases = []

      try {
        let hasMore = true
        while (hasMore) {
          // eslint-disable-next-line no-await-in-loop
          const response = await axios.get(`${constants.baseUrlV2}/debt-cases/list?perPage=${pageSize}&page=${page}`)

          if (response.status === 200) {
            const responseData = response.data.data
            const apiCases = responseData.data || responseData || []

            if (!apiCases || apiCases.length === 0) {
              hasMore = false
              break
            }

            // Exclude closed cases (status 3)
            const validCases = apiCases.filter(c => [1, 4].includes(c.status))
            const mappedCases = validCases.map(caseItem => this.mapCaseData(caseItem))
            allCases = [...allCases, ...mappedCases]

            if (apiCases.length < pageSize) {
              hasMore = false
            } else {
              page += 1
            }
          } else {
            hasMore = false
          }
        }

        // Show cached results instantly if available
        const cached = this.loadCache()
        if (cached && allCases.length > 0) {
          this.cases = allCases.filter(c => cached.caseIds.includes(c.id))
          cached.caseIds.forEach(id => {
            if (cached.docs[id]) {
              this.caseDocuments[id] = cached.docs[id]
            }
          })
          this.loading = false
        }

        // Check documents for all cases, progressively adding matches
        await this.checkDocuments(allCases)
      } catch (error) {
        console.error('Error fetching cases:', error)
      } finally {
        this.loading = false
      }
    },

    async checkDocuments(allCases) {
      const batchSize = 10
      const foundCaseIds = []

      for (let i = 0; i < allCases.length; i += batchSize) {
        const batch = allCases.slice(i, i + batchSize)

        // eslint-disable-next-line no-await-in-loop
        const results = await Promise.all(
          batch.map(async caseItem => {
            try {
              const res = await axios.get(
                `${constants.baseUrl}/debt-case/docs/list`,
                { params: { debt_case_id: caseItem.id } },
              )
              const data = res.data?.data
              const docs = Array.isArray(data) ? data : []
              const filtered = docs.filter(doc => doc.category === 'KS Debitor')
              return { caseId: caseItem.id, docs: filtered }
            } catch {
              return { caseId: caseItem.id, docs: [] }
            }
          }),
        )

        // Add matching cases progressively
        results.forEach(result => {
          this.caseDocuments[result.caseId] = result.docs
          if (result.docs.length > 0) {
            foundCaseIds.push(result.caseId)
            const caseData = allCases.find(c => c.id === result.caseId)
            if (caseData && !this.cases.find(c => c.id === result.caseId)) {
              this.cases.push(caseData)
            }
          }
        })

        // Hide loading after first batch
        this.loading = false
      }

      // Remove cached cases that no longer have documents
      this.cases = this.cases.filter(c => foundCaseIds.includes(c.id))

      // Save to cache for next visit
      this.saveCache(foundCaseIds)
    },

    loadCache() {
      try {
        const raw = sessionStorage.getItem('debtia_doc_cache')
        if (!raw) return null
        const data = JSON.parse(raw)
        // Cache valid for 5 minutes
        if (Date.now() - data.timestamp > 5 * 60 * 1000) return null
        return data
      } catch {
        return null
      }
    },

    saveCache(caseIds) {
      try {
        sessionStorage.setItem('debtia_doc_cache', JSON.stringify({
          timestamp: Date.now(),
          caseIds,
          docs: this.caseDocuments,
        }))
      } catch {
        // sessionStorage full or unavailable
      }
    },

    mapCaseData(caseItem) {
      const { statements = [] } = caseItem
      const excludedRows = ['0000', '9999', '99999']
      const relevantStatements = statements.filter(s => !excludedRows.includes(s.job_task_no))
      const totalAmount = relevantStatements.reduce((sum, s) => sum + (parseFloat(s.schedule_total_price) || 0), 0)
      const paidAmount = relevantStatements.reduce((sum, s) => sum + (parseFloat(s.paid_amount) || 0), 0)
      const remainingAmount = totalAmount - paidAmount

      return {
        id: caseItem.id,
        creditorName: caseItem.client_name || this.$t('DEBTIA_UNKNOWN_CREDITOR'),
        journalNumber: caseItem.id,
        totalAmount,
        paidAmount,
        remainingAmount,
        status: caseItem.status,
      }
    },

    selectCase(caseItem) {
      this.selectedCase = caseItem
      this.documents = this.caseDocuments[caseItem.id] || []
    },

    handleDocClick(doc) {
      if (doc.file_link) {
        window.open(doc.file_link, '_blank')
      }
    },

    getDocName(doc) {
      return doc.title || doc.file_name || this.$t('DEBTIA_DOCUMENT')
    },

    getDocCategory(doc) {
      if (doc.category) return doc.category
      const stages = {
        base_documents: this.$t('DEBTIA_DOC_BASE'),
        collection: this.$t('DEBTIA_DOC_COLLECTION'),
        correspondence: this.$t('DEBTIA_DOC_CORRESPONDENCE'),
        legal: this.$t('DEBTIA_DOC_LEGAL'),
      }
      return stages[doc.debt_stage] || doc.debt_stage || null
    },

    getDocIcon(doc) {
      const name = (doc.file_name || '').toLowerCase()
      if (name.endsWith('.pdf')) return 'FileIcon'
      if (name.endsWith('.docx') || name.endsWith('.doc')) return 'FileTextIcon'
      if (name.endsWith('.xlsx') || name.endsWith('.xls')) return 'GridIcon'
      if (name.endsWith('.jpg') || name.endsWith('.png') || name.endsWith('.jpeg')) return 'ImageIcon'
      return 'FileTextIcon'
    },

    getFileExtension(doc) {
      const name = doc.file_name || ''
      const ext = name.split('.').pop()
      return ext ? ext.toUpperCase() : ''
    },

    formatCurrency(value) {
      if (!value && value !== 0) return '—'
      return new Intl.NumberFormat('da-DK', {
        style: 'currency',
        currency: 'DKK',
        minimumFractionDigits: 2,
      }).format(value)
    },
  },
}
</script>

<style lang="scss" scoped>
.documents-page {
  min-height: 400px;

  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.2s ease;
  }

  .fade-enter,
  .fade-leave-to {
    opacity: 0;
  }

  // Page Header
  .docs-page-header {
    margin-bottom: 1.5rem;

    .page-title {
      font-weight: 600;
      color: #5e5873;
      margin-bottom: 0.25rem;
      display: flex;
      align-items: center;

      .cases-count {
        font-weight: 400;
        color: #a0a0a0;
        margin-left: 0.35rem;
        font-size: 0.85em;
      }
    }

    .page-subtitle {
      font-size: 0.85rem;
      color: #a0a0a0;
      margin: 0;
    }
  }

  // Back Button
  .back-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    background: none;
    border: none;
    padding: 0.5rem 0;
    margin-bottom: 1rem;
    font-size: 0.88rem;
    font-weight: 500;
    color: #088c76;
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover {
      color: #002954;
      gap: 0.65rem;
    }
  }

  // Case Detail Header
  .case-detail-header {
    background:
      radial-gradient(ellipse 70% 200% at 10% 10%, #088c76 0%, transparent 60%),
      radial-gradient(ellipse 100% 140% at 80% 30%, #002954 0%, transparent 100%);
    background-color: #000;
    border-radius: 1rem;
    padding: 1.5rem 1.75rem;
    margin-bottom: 1.5rem;

    .detail-header-content {
      display: flex;
      align-items: center;
      gap: 1rem;
    }

    .detail-icon {
      width: 2.4rem;
      height: 2.4rem;
      border-radius: 0.6rem;
      background: rgba(255, 255, 255, 0.15);
      border: 1px solid rgba(255, 255, 255, 0.25);
      display: flex;
      align-items: center;
      justify-content: center;
      color: #fff;
      flex-shrink: 0;
    }

    .detail-info {
      .detail-creditor {
        font-size: 1.15rem;
        font-weight: 700;
        color: #fff;
        margin: 0 0 0.15rem;
      }

      .detail-meta {
        font-size: 0.82rem;
        color: rgba(255, 255, 255, 0.7);

        .meta-sep {
          margin: 0 0.4rem;
          opacity: 0.5;
        }
      }
    }
  }

  // Cases Grid
  .cases-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(min(300px, 100%), 1fr));
    gap: 1rem;
  }

  .doc-case-card {
    background: rgba(255, 255, 255, 0.85);
    backdrop-filter: blur(16px);
    -webkit-backdrop-filter: blur(16px);
    border: 1px solid rgba(255, 255, 255, 0.4);
    border-radius: 0.875rem;
    overflow: hidden;
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
      border-color: rgba(8, 140, 118, 0.2);

      .card-arrow {
        color: #088c76;
        transform: translateX(2px);
      }
    }

    .card-header-bar {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 1rem 1.25rem;
      border-bottom: 1px solid rgba(0, 0, 0, 0.05);

      .card-creditor {
        font-size: 1rem;
        font-weight: 600;
        color: #002954;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .card-arrow {
        color: #a0a0a0;
        flex-shrink: 0;
        transition: all 0.2s ease;
      }
    }

    .card-details {
      padding: 1rem 1.25rem;

      .card-detail-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 0.25rem 0;

        .detail-label {
          font-size: 0.82rem;
          color: #a0a0a0;
        }

        .detail-value {
          font-size: 0.88rem;
          font-weight: 500;
          color: #5e5873;

          &.amount {
            font-weight: 700;
            color: #002954;
          }
        }
      }
    }
  }

  // Empty state
  .docs-empty {
    text-align: center;
    padding: 4rem 2rem;
    background: rgba(255, 255, 255, 0.7);
    border-radius: 1rem;
    backdrop-filter: blur(20px);

    .empty-icon {
      color: #c0c0c0;
      margin-bottom: 1rem;
    }

    h5 {
      font-weight: 600;
      color: #5e5873;
      margin-bottom: 0.5rem;
    }

    p {
      color: #a0a0a0;
      margin: 0;
      font-size: 0.88rem;
    }
  }

  // Documents List
  .docs-list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;

    .doc-item {
      display: flex;
      align-items: center;
      gap: 1rem;
      padding: 1rem 1.25rem;
      background: rgba(255, 255, 255, 0.85);
      backdrop-filter: blur(16px);
      border: 1px solid rgba(255, 255, 255, 0.4);
      border-radius: 0.75rem;
      cursor: pointer;
      transition: all 0.2s ease;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03);

      &:hover {
        background: rgba(255, 255, 255, 0.95);
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
        border-color: rgba(8, 140, 118, 0.15);

        .doc-action {
          color: #088c76;
        }
      }

      .doc-icon {
        width: 42px;
        height: 42px;
        border-radius: 10px;
        background: rgba(8, 140, 118, 0.08);
        color: #088c76;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
      }

      .doc-info {
        flex: 1;
        min-width: 0;

        .doc-name {
          display: block;
          font-size: 0.9rem;
          font-weight: 500;
          color: #002954;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .doc-category {
          display: block;
          font-size: 0.76rem;
          color: #a0a0a0;
          margin-top: 0.15rem;
        }
      }

      .doc-ext {
        font-size: 0.68rem;
        font-weight: 700;
        color: #a0a0a0;
        background: rgba(0, 0, 0, 0.04);
        padding: 0.2rem 0.5rem;
        border-radius: 4px;
        letter-spacing: 0.03em;
        flex-shrink: 0;
      }

      .doc-action {
        color: #c0c0c0;
        flex-shrink: 0;
        transition: color 0.15s ease;
      }
    }
  }
}
</style>
